import { sessionsApi } from "@features/chat/services/api";
import { toast } from "@stores/toastStore";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

// Session cache keys are scope-parameterised so switching workspace via
// OrgSwitcher re-keys the list query (each scope gets its own cache
// entry). "personal" is the sentinel for the caller's personal chats.
type Scope = string | "personal";

function scopeKey(organisationId?: string | null): Scope {
  return organisationId ? organisationId : "personal";
}

// `projectFilter` distinguishes three list views that must not share cache:
//   undefined → no project filter (HistoryPage shows everything in scope)
//   null      → unscoped-only (standalone /chat)
//   string    → that specific project (/projects/$id/chat)
function projectKey(projectFilter?: string | null): string {
  if (projectFilter === undefined) return "all";
  if (projectFilter === null) return "unscoped";
  return projectFilter;
}

type SurfaceFilter = "workbench" | "requirements" | null | undefined;

function surfaceKey(surface: SurfaceFilter): string {
  return surface ?? "any";
}

export const sessionKeys = {
  all: ["sessions"] as const,
  lists: (
    organisationId?: string | null,
    projectFilter?: string | null,
    surface?: SurfaceFilter,
  ) =>
    [
      ...sessionKeys.all,
      "list",
      scopeKey(organisationId),
      projectKey(projectFilter),
      surfaceKey(surface),
    ] as const,
  messages: (id: string) => [...sessionKeys.all, "messages", id] as const,
};

function errorMessage(err: unknown, fallback: string): string {
  if (err instanceof Error && err.message) return err.message;
  return fallback;
}

export function useSessions(
  organisationId?: string | null,
  projectFilter?: string | null,
  surfaceFilter?: SurfaceFilter,
) {
  return useQuery({
    queryKey: sessionKeys.lists(organisationId, projectFilter, surfaceFilter),
    queryFn: () =>
      sessionsApi.list(organisationId, projectFilter, surfaceFilter ?? undefined),
  });
}

/**
 * Fetch one (paginated) page of messages for a session. Returns the raw
 * `MessagesPage` envelope so callers can drive a "load older" affordance
 * if they need it; consumers that just want the array can read `.messages`.
 */
export function useSessionMessages(id: string, enabled = true) {
  return useQuery({
    queryKey: sessionKeys.messages(id),
    queryFn: () => sessionsApi.getMessages(id),
    enabled: enabled && !!id,
  });
}

export function useCreateSession() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data?: {
      title?: string;
      project_id?: string;
      organisation_id?: string | null;
      surface?: "workbench" | "requirements" | null;
      /** Multi-select board scope. */
      board_ids?: string[];
    }) => sessionsApi.create(data),
    onSuccess: () => {
      // Nuke every scope's list cache — the newly created session could
      // belong to any scope and partial-key invalidation already wipes
      // nested keys, so this is a scope-agnostic refresh.
      void qc.invalidateQueries({ queryKey: sessionKeys.all });
    },
    onError: (err) => {
      toast.error(errorMessage(err, "Could not create session"));
    },
  });
}

export function useDeleteSession() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (sessionId: string) => sessionsApi.delete(sessionId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: sessionKeys.all });
      toast.success("Session deleted");
    },
    onError: (err) => {
      toast.error(errorMessage(err, "Could not delete session"));
    },
  });
}

export function useUpdateSessionTitle() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ sessionId, title }: { sessionId: string; title: string }) =>
      sessionsApi.updateTitle(sessionId, title),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: sessionKeys.all });
      toast.success("Session renamed");
    },
    onError: (err) => {
      toast.error(errorMessage(err, "Could not rename session"));
    },
  });
}

// `useUpdateSessionTools` was retired in May 2026 with the workbench
// tool picker. The corresponding server endpoint
// `PATCH /sessions/{id}/tools` returns 410 Gone. Mid-session loadout
// edits no longer exist; the agent has every tool baseline.
